// MAP SYSTEM — render-side contract and shared building blocks (owner 2026-11-02: 8 arenas, each in src/maps/<id>/).
// A map's render.ts exports `RENDER: MapRenderDef`: its four lighting presets (dawn / noon / dusk / night), an environment
// builder (sky extras, ground colour, distant scenery, ambient particles), and optional reskins for the five prop archetypes,
// the enemy families and the final boss, plus set-piece visuals for its hazards. Render-only: reads Sim state, never writes it.
// See Docs/DESIGN/MAPS.md for the guide the map agents follow.
import * as THREE from 'three/webgpu';
import {
  color, dot, float, max, mix, mx_noise_float, mx_worley_noise_float, positionWorld, positionWorldDirection, pow, smoothstep, time, uniform, vec3,
} from 'three/tsl';
import type { EnemyKind } from '../core/content';
import type { DayLook, TodChoice } from '../core/mapdef';
import { ARENA_R } from '../core/sim';
import type { Sim } from '../core/sim';
import type { HazardZone } from '../core/hazards';
import type { GroundDeform } from './ground';
import { GROUND_HALF, nearGroundTiles } from './ground';
import { buildEdgeWall, cliffPiece, rubblePiece } from './edgeWall';
import type { RenderStyle } from './toon';
import { toonGradient as toonGradientMap } from './toon';
import type { FloraShape } from './flora';
import type { ArtBuilder, EnemyArt } from './enemyArt/kit';
import type { Fx } from './fx';
import type { DebrisLook } from './debrisLook';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Node = any;

// ------------------------------------------------------------------------------------------------ lighting presets
/** One time-of-day look. Colours are hex sRGB. Every field is blended for the live cycle. */
export interface LightPreset {
  /** sky gradient: zenith, middle band, horizon, below the horizon */
  zen: number; mid: number; hor: number; below: number;
  /** sun disc + halo colours (the sky term; set both to black at night) */
  sunCore: number; sunHalo: number;
  /** cloud underside / lit top, and cloud cover (0 = none, 1 = the map #1 amount) */
  cloudLo: number; cloudHi: number; clouds: number;
  /** star field 0..1 and moon disc 0..1 (night) */
  stars: number; moon: number;
  /** hemisphere fill (sky / ground colour, intensity) */
  hemiSky: number; hemiGround: number; hemiI: number;
  /** key light (the sun by day, the moon at night): colour, intensity, direction (towards the light) */
  sun: number; sunI: number; sunDir: [number, number, number];
  /** distance fog colour and range (m) */
  fog: number; fogNear: number; fogFar: number;
  /** tone-mapping exposure and bloom multipliers (1 = map #1 day) */
  exposure: number; bloom: number;
  /** night readability 0..1: bright rim + cool outline on foes, stronger skill lights */
  rim: number;
  /** soft light pool around the hero (intensity; 0 by day) */
  heroLight: number;
  /** multiplier for unlit painted materials (foliage cards): white by day, moonlit blue at night */
  paint: number;
}
export interface MapLook { dawn: LightPreset; noon: LightPreset; dusk: LightPreset; night: LightPreset }

const _ca = new THREE.Color(), _cb = new THREE.Color();
const lerpHex = (a: number, b: number, t: number) => _ca.setHex(a).lerp(_cb.setHex(b), t).getHex();
const NUM: (keyof LightPreset)[] = ['clouds', 'stars', 'moon', 'hemiI', 'sunI', 'fogNear', 'fogFar', 'exposure', 'bloom', 'rim', 'heroLight'];
const HEX: (keyof LightPreset)[] = ['zen', 'mid', 'hor', 'below', 'sunCore', 'sunHalo', 'cloudLo', 'cloudHi', 'hemiSky', 'hemiGround', 'sun', 'fog', 'paint'];
export function lerpPreset(a: LightPreset, b: LightPreset, t: number, out: LightPreset = { ...a, sunDir: [...a.sunDir] }): LightPreset {
  if (t <= 0) { Object.assign(out, a, { sunDir: [...a.sunDir] }); return out; }
  if (t >= 1) { Object.assign(out, b, { sunDir: [...b.sunDir] }); return out; }
  const o = out as unknown as Record<string, number>, A = a as unknown as Record<string, number>, B = b as unknown as Record<string, number>;
  for (const k of NUM) o[k] = A[k] + (B[k] - A[k]) * t;
  for (const k of HEX) o[k] = lerpHex(A[k], B[k], t);
  const d = [0, 1, 2].map((i) => a.sunDir[i] + (b.sunDir[i] - a.sunDir[i]) * t), l = Math.hypot(d[0], d[1], d[2]) || 1;
  out.sunDir = [d[0] / l, d[1] / l, d[2] / l];
  return out;
}
/** Key frames of the live cycle (clock: 0 midnight .. 0.5 noon). Fixed choices land exactly on a key (no blending). */
const KEYS: [number, keyof MapLook][] = [[0, 'night'], [0.17, 'night'], [0.27, 'dawn'], [0.37, 'noon'], [0.63, 'noon'], [0.73, 'dusk'], [0.83, 'night'], [1, 'night']];
export function presetAt(look: MapLook, clock: number, out?: LightPreset): LightPreset {
  const c = clock - Math.floor(clock);
  for (let i = 0; i < KEYS.length - 1; i++) {
    const [c0, k0] = KEYS[i], [c1, k1] = KEYS[i + 1];
    if (c <= c1) { const u = (c - c0) / (c1 - c0); return lerpPreset(look[k0], look[k1], u * u * (3 - 2 * u), out); }
  }
  return lerpPreset(look.night, look.night, 0, out);
}
/** The look a fixed choice shows (select-page thumbnails, ?mapcheck). */
export const lookFor = (look: MapLook, choice: TodChoice, day: DayLook): LightPreset => (choice === 'night' ? look.night : look[day]);

// ------------------------------------------------------------------------------------------------ sky
export interface SkyUniforms {
  zen: Node; mid: Node; hor: Node; below: Node; sunCore: Node; sunHalo: Node; cloudLo: Node; cloudHi: Node; clouds: Node; stars: Node; moon: Node;
  sunDir: Node; moonDir: Node; moonSize: Node; moonCol: Node;
}
export interface Sky { node: Node; u: SkyUniforms; apply(p: LightPreset): void }
export interface SkyOpts {
  /** cloud texture scale (1 = map #1) */ cloudScale?: number;
  /** moon disc angular radius (rad; ~0.035 = normal, 0.09 = huge) and colour */ moonSize?: number; moonColor?: number;
  /** direction to the moon (fixed; the key light follows the preset's sunDir) */ moonDir?: [number, number, number];
  /** add map-specific sky features (a second sun, auroras…): gets the view direction and the sky colour so far */
  extra?: (dir: Node, sky: Node, u: SkyUniforms) => Node;
}
/** The painted sky dome every map shares: gradient, sun disc + halo, drifting cumulus, star field and moon. With stars = moon
 *  = 0 and clouds = 1 it is exactly the original Canyon Meadow sky. Set as scene.backgroundNode. */
export function buildSky(opts: SkyOpts = {}): Sky {
  const C = (h: number) => uniform(new THREE.Color(h));
  const u: SkyUniforms = {
    zen: C(0x0f78e8), mid: C(0x3aa8f5), hor: C(0xc6ecff), below: C(0x7aa86a), sunCore: C(0xfff4d8), sunHalo: C(0xffe6b0), cloudLo: C(0xdce9f6), cloudHi: C(0xffffff),
    clouds: uniform(1), stars: uniform(0), moon: uniform(0), sunDir: uniform(new THREE.Vector3(-0.45, 0.75, -0.45).normalize()),
    moonDir: uniform(new THREE.Vector3(...(opts.moonDir ?? [0.35, 0.62, -0.7])).normalize()), moonSize: uniform(opts.moonSize ?? 0.035), moonCol: C(opts.moonColor ?? 0xeef4ff),
  };
  const dir = positionWorldDirection, up = dir.y;
  let sky: Node = mix(u.hor, u.mid, smoothstep(0.0, 0.16, up));
  sky = mix(sky, u.zen, smoothstep(0.14, 0.55, up));
  const sd = max(dot(dir, u.sunDir), 0.0);
  sky = sky.add(u.sunCore.mul(pow(sd, 32.0).mul(1.6))).add(u.sunHalo.mul(pow(sd, 5.0).mul(0.12)));
  // night: star points (worley cell centres) that thin out toward the horizon, and the moon disc + soft halo
  const st = smoothstep(0.075, 0.0, mx_worley_noise_float(dir.mul(110.0))).mul(mx_noise_float(dir.mul(40.0).add(time.mul(0.6))).mul(0.4).add(0.75));
  sky = sky.add(vec3(0.85, 0.9, 1.0).mul(st.mul(smoothstep(0.04, 0.3, up)).mul(u.stars).mul(1.4)));
  const md = dot(dir, u.moonDir), mr = u.moonSize;
  const disc = smoothstep(mr.cos(), mr.mul(0.92).cos(), md);
  const crater = mx_noise_float(dir.mul(55.0)).mul(0.12).add(0.9);
  sky = mix(sky, u.moonCol.mul(crater).mul(1.25), disc.mul(u.moon));
  sky = sky.add(u.moonCol.mul(pow(max(md, 0.0), 90.0).mul(0.22).add(pow(max(md, 0.0), 12.0).mul(0.05))).mul(u.moon));
  // cumulus: two octaves of drifting noise, soft-edged puffs with a shaded underside
  const cuv = dir.xz.div(up.max(0.06)).mul(0.55 * (opts.cloudScale ?? 1)).add(time.mul(0.008));
  const c1 = mx_noise_float(cuv).mul(0.5).add(0.5), c2 = mx_noise_float(cuv.mul(3.1).add(7.0)).mul(0.5).add(0.5);
  const cl = c1.mul(0.75).add(c2.mul(0.25));
  const puff = smoothstep(0.56, 0.62, cl).mul(smoothstep(0.02, 0.16, up));
  const shade = smoothstep(0.62, 0.7, cl);
  sky = mix(sky, mix(u.cloudLo, u.cloudHi, shade), puff.mul(0.95).mul(u.clouds));
  if (opts.extra) sky = opts.extra(dir, sky, u);
  sky = mix(sky, u.below, smoothstep(-0.02, -0.25, up));
  const apply = (p: LightPreset) => {
    for (const k of ['zen', 'mid', 'hor', 'below', 'sunCore', 'sunHalo', 'cloudLo', 'cloudHi'] as const) u[k].value.setHex(p[k]);
    u.clouds.value = p.clouds; u.stars.value = p.stars; u.moon.value = p.moon;
    u.sunDir.value.set(...p.sunDir).normalize();
  };
  return { node: sky, u, apply };
}

// ------------------------------------------------------------------------------------------------ environment
export interface EnvCtx {
  scene: THREE.Scene; shadows: boolean; style: RenderStyle; quality: string; ground: GroundDeform;
  /** 3D grass / tuft density tier (Settings › Advanced › Grass density; defaults to the quality preset). 'low' = none. */
  grass?: string;
  /** ?probe=… debug switches (e.g. 'nosky') */ probe: string;
  /** the view camera (EDGE WALLS: chunk culling of the wall pieces under its footprint) */
  camera?: THREE.PerspectiveCamera;
}
export interface MapEnv {
  /** everything the env added (removed + disposed on map change) */
  group: THREE.Group;
  sky: Sky | null;
  /** per frame: t = seconds, focus = camera target, hero = hero position; preset = the current look (already applied to
   *  sky / lights / fog); night = 0..1 */
  update(t: number, focus: THREE.Vector3, hero: THREE.Vector3, preset: LightPreset, night: number): void;
  dispose?(): void;
}

/** Replacement shapes for the five prop archetypes (the sim footprints stay as the core spec places them). Each entry is
 *  one instanced part: geometry (unit size, the archetype's transform scales it), material, ink outline thickness (cel). */
export type PropParts = [THREE.BufferGeometry, THREE.Material, number][];
export interface PropSkin {
  /** tree / bush variants (index = the core spec's variants order); see render/flora.ts for the shape contract */
  trees?: () => FloraShape[]; bushes?: () => FloraShape[];
  rock?: () => PropParts; boulder?: () => PropParts; spire?: () => PropParts;
  /** debris pieces for breaking rocks / boulders / spires: outer and inner (fracture) materials */
  debrisMats?: Partial<Record<'rock' | 'boulder' | 'spire', [THREE.Material, THREE.Material]>>;
  /** colour of break dust / chips per archetype */
  dust?: Partial<Record<'tree' | 'bush' | 'rock' | 'boulder' | 'spire', number>>;
}
/** Enemy reskin: same bodies and animation, recoloured (exact colour swaps from the family palettes, then an optional
 *  tint) and optionally re-dressed (extra parts added with the family kit, e.g. ice crusts, ash cloaks). */
export interface EnemySkin {
  /** exact vertex-colour swaps: { 0xc07a3a: 0x5a8cff } (see render/enemyArt/kit.ts PAL for the family colours) */
  swap?: Record<number, number>;
  /** multiply every body colour by this (r, g, b) after the swaps */
  tint?: [number, number, number];
  /** glow parts (eyes, cores) recolour: multiply by this */
  glow?: [number, number, number];
  /** extra parts per family / variant; add them to `b` (positioned in the family's model space, feet at y = 0) */
  dress?: (kind: EnemyKind, elite: boolean, b: ArtBuilder) => void;
}
/** Set-piece visuals for this map's hazards (collapsing towers, lava cracks, storm walls). The default telegraph rings
 *  are always drawn on top, so a map can skip this entirely. */
export interface HazardVisual {
  update(sim: Sim, zones: readonly HazardZone[], dt: number, t: number): void;
  /** a zone changed phase (warn / active / fire / end) */
  event?(z: HazardZone, phase: 'warn' | 'active' | 'fire' | 'end', fx: Fx): void;
  dispose(): void;
}

export interface MapRenderDef {
  /** must match the folder / core id */
  id: string;
  look: MapLook;
  /** sky + ground + scenery + ambient particles */
  buildEnv(ctx: EnvCtx): MapEnv;
  props?: PropSkin;
  enemies?: EnemySkin;
  /** the final boss's look (default: the Hollow Tyrant) */
  bossArt?: EnemyArt;
  /** debris palette: falling leaves, ash, blast grit / turf / dust, clods, chips, rubble, foliage puffs (default: meadow) */
  debris?: Partial<DebrisLook>;
  hazards?: (ctx: EnvCtx) => HazardVisual;
}

/** Map vistas (cliffs, buttes, skylines) were authored around the 110 m arena. The arena grew to ARENA_R (owner 2026-11-02:
 *  160 m); scenery placed at an absolute radius adds VISTA_SHIFT so its distance beyond the wall stays as designed. */
export const VISTA_REF_R = 110;
export const VISTA_SHIFT = ARENA_R - VISTA_REF_R;
/** circumference ratio of a vista ring at radius r (authored at 110 m) — scale ring instance counts by it to keep spacing */
export const vistaRingK = (r: number): number => (r + VISTA_SHIFT) / r;
/** outer radius of the flat far ground ring (was 400 m at the 110 m arena; stays inside the 600 m camera far plane) */
export const FAR_GROUND_R = 400 + VISTA_SHIFT;

// ------------------------------------------------------------------------------------------------ shared ground pieces
/** Hills / crater / char shading every map's ground uses on top of its own colour (same as the original arena). */
export function groundMarks(col: Node, gd: ReturnType<GroundDeform['nodes']>, o: { dig?: [number, number]; lip?: number; char?: [number, number]; /** ICE FAMILY: frost colours (bright, shaded) */ frost?: [number, number] } = {}): Node {
  const p = positionWorld.xz, n3 = mx_noise_float(p.mul(2.2)).mul(0.5).add(0.5);
  const top = smoothstep(0.5, 1.9, gd.base), low = smoothstep(-0.1, -0.8, gd.base), steep = smoothstep(0.995, 0.965, gd.slope);
  col = mix(col, col.mul(1.1).add(vec3(0.03, 0.035, 0.0)), top.mul(0.45));
  col = mix(col, col.mul(vec3(0.8, 0.86, 0.88)), low.mul(0.55));
  col = col.mul(float(1).sub(steep.mul(0.1)));
  const dig = smoothstep(-0.04, -0.9, gd.deform), lip = smoothstep(0.04, 0.35, gd.deform);
  const [d0, d1] = o.dig ?? [0x8a6a48, 0x5a4230];
  col = mix(col, mix(color(d0), color(d1), smoothstep(-0.6, -2.4, gd.deform)), dig);
  col = mix(col, color(o.lip ?? 0xb08c62), lip.mul(0.8));
  const charN = mx_noise_float(p.mul(0.9).add(7.3)).mul(0.5).add(0.5);
  const charA = smoothstep(0.12, 0.55, gd.burnSoft.add(charN.sub(0.5).mul(0.28)));
  const [c0, c1] = o.char ?? [0x3a3024, 0x221a14];
  col = mix(col, mix(color(c0), color(c1), n3), charA.mul(0.85));
  // ICE FAMILY (owner 2026-11-03): frost on the grass, an icy blue-white rime with a crystalline grain (the A channel of the
  // ground map, GroundDeform.stampFrost); it sits on top of char so a frozen scorch reads as frosted
  const frostN = mx_noise_float(p.mul(3.1).add(2.7)).mul(0.5).add(0.5);
  const frostA = smoothstep(0.1, 0.5, gd.frostSoft.add(frostN.sub(0.5).mul(0.22)));
  const [f0, f1] = o.frost ?? [0xdff6ff, 0x9fd8f0];
  return mix(col, mix(color(f0), color(f1), frostN), frostA.mul(0.9));
}
/** The deformable near ground (hills, craters, burn) plus a flat far ring, both coloured by `mat` (whose colorNode the map
 *  set). Returns the near ground (a group of frustum-culled tiles, render/ground.ts nearGroundTiles). */
export function groundMeshes(group: THREE.Group, ctx: EnvCtx, mat: THREE.NodeMaterial): THREE.Object3D {
  const gd = ctx.ground.nodes();
  const farMat = mat.clone(); farMat.colorNode = mat.colorNode;
  const far = new THREE.Mesh(new THREE.RingGeometry(GROUND_HALF - 1, FAR_GROUND_R, 160, 1).rotateX(-Math.PI / 2).translate(0, -0.06, 0), farMat);
  far.receiveShadow = true; group.add(far);
  mat.positionNode = gd.position; mat.normalNode = gd.normal;
  const near = nearGroundTiles(mat); group.add(near); // BIGMAP: 36 frustum-culled tiles at ~0.86 m spacing (was one 300² plane)
  return near;
}
/** The energy barrier at the arena edge (ring on the ground + a low glowing wall). */
export function arenaBarrier(group: THREE.Group, ring = 0x58b8ff, wall = 0x3a90ff): void {
  const ringMat = new THREE.MeshBasicNodeMaterial({ transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
  ringMat.colorNode = color(ring).mul(time.mul(2).sin().mul(0.25).add(0.75)).mul(1.6);
  ringMat.opacityNode = float(0.55);
  const wallGeo = new THREE.CylinderGeometry(ARENA_R + 0.6, ARENA_R + 0.6, 2.5, 192, 1, true); wallGeo.translate(0, 1.25, 0);
  const wallMat = new THREE.MeshBasicNodeMaterial({ transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
  wallMat.colorNode = color(wall).mul(smoothstep(2.5, 0.0, positionWorld.y)).mul(time.mul(1.5).add(positionWorld.x.mul(0.2)).sin().mul(0.2).add(0.5));
  group.add(new THREE.Mesh(wallGeo, wallMat));
  group.add(new THREE.Mesh(new THREE.TorusGeometry(ARENA_R + 0.6, 0.12, 6, 240).rotateX(Math.PI / 2).translate(0, 0.05, 0), ringMat));
}
/** Free GPU resources of everything under a group (map change). Shared textures / cached materials may be skipped. */
export function disposeGroup(g: THREE.Object3D): void {
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
    for (const x of mats) x.dispose();
  });
  g.removeFromParent();
}

/** Quick preset builder for maps: start from a base and override a few fields. */
export const preset = (base: LightPreset, o: Partial<LightPreset>): LightPreset => ({ ...base, ...o, sunDir: o.sunDir ?? [...base.sunDir] });

// ------------------------------------------------------------------------------------------------ starter look + env
const NOON0: LightPreset = {
  zen: 0x0f78e8, mid: 0x3aa8f5, hor: 0xc6ecff, below: 0x7aa86a, sunCore: 0xfff4d8, sunHalo: 0xffe6b0, cloudLo: 0xdce9f6, cloudHi: 0xffffff, clouds: 1, stars: 0, moon: 0,
  hemiSky: 0xd8ecff, hemiGround: 0x4e6a34, hemiI: 1.9, sun: 0xfff0d8, sunI: 2.6, sunDir: [-0.45, 0.75, -0.45],
  fog: 0xc6ecff, fogNear: 130, fogFar: 520, exposure: 1, bloom: 1, rim: 0, heroLight: 0, paint: 0xffffff,
};
/** Starting presets for a new map (the Canyon Meadow numbers): copy with preset(BASE_LOOK.noon, { …overrides }). Night keeps
 *  the readability floor every map should respect: hemiI >= ~1.1, a moonlit key light >= ~1.2, rim 1, a hero light pool. */
export const BASE_LOOK: MapLook = {
  noon: NOON0,
  dawn: preset(NOON0, { zen: 0x3560c0, mid: 0x93b0e6, hor: 0xffd2b0, sunCore: 0xfff0d6, sunHalo: 0xffb888, cloudLo: 0xd6a8bc, cloudHi: 0xffe8cc, below: 0x6a8a5e, fog: 0xf2cdb8,
    hemiSky: 0xc4d2f2, hemiGround: 0x56603a, hemiI: 1.6, sun: 0xffc896, sunI: 2.35, sunDir: [0.62, 0.4, 0.58] }),
  dusk: preset(NOON0, { zen: 0x2e3a8c, mid: 0x9a6ab8, hor: 0xffb47a, sunCore: 0xffe2b0, sunHalo: 0xff9a5a, cloudLo: 0xb8789a, cloudHi: 0xffd2a0, below: 0x5e6c4a, fog: 0xe89a86,
    hemiSky: 0xa890d8, hemiGround: 0x5a3a34, hemiI: 1.45, sun: 0xff8a3c, sunI: 2.7, sunDir: [-0.62, 0.42, -0.66] }),
  night: preset(NOON0, { zen: 0x040a22, mid: 0x10245a, hor: 0x2a4880, below: 0x1a2a2a, sunCore: 0x000000, sunHalo: 0x000000, cloudLo: 0x141e3a, cloudHi: 0x3c4c78, clouds: 0.55, stars: 1, moon: 1,
    hemiSky: 0x4c62b4, hemiGround: 0x1a2036, hemiI: 1.1, sun: 0x8eaeff, sunI: 1.2, sunDir: [0.35, 0.62, -0.7], fog: 0x16224a, fogNear: 70, fogFar: 340, exposure: 1.05, bloom: 1.2, rim: 1, heroLight: 8, paint: 0x6a7cb8 }),
};
/** Recolour all four presets at once (sky gradient + fog by time of day) — handy for placeholders and first passes. */
export function tintLook(base: MapLook, o: Partial<Record<keyof MapLook, Partial<LightPreset>>>): MapLook {
  return { dawn: preset(base.dawn, o.dawn ?? {}), noon: preset(base.noon, o.noon ?? {}), dusk: preset(base.dusk, o.dusk ?? {}), night: preset(base.night, o.night ?? {}) };
}

export interface BasicEnvOpts {
  /** ground: base, light patches, dark patches (hex) */ ground?: [number, number, number];
  /** crater bowl colours, lip colour, char colours (groundMarks) */ marks?: Parameters<typeof groundMarks>[2];
  /** distant butte colour (0 = no scenery) */ scenery?: number;
  /** barrier ring / wall colours */ barrier?: [number, number];
  sky?: SkyOpts;
}
/** A plain, cheap environment: shared sky, a two-tone noise ground with the shared crater / burn marks, the barrier and a
 *  ring of distant buttes. Placeholder maps use it; real maps usually start from it and add their own ground and scenery. */
export function basicEnv(ctx: EnvCtx, o: BasicEnvOpts): MapEnv {
  const group = new THREE.Group(); ctx.scene.add(group);
  const sky = buildSky(o.sky ?? {});
  if (!ctx.probe.includes('nosky')) ctx.scene.backgroundNode = sky.node;
  const [g0, g1, g2] = o.ground ?? [0x6a9a58, 0x8cbf6a, 0x4a7a44];
  const p = positionWorld.xz;
  const big = mx_noise_float(p.mul(0.02).add(1.3)).mul(0.5).add(0.5), fine = mx_noise_float(p.mul(0.3)).mul(0.5).add(0.5);
  let col = mix(color(g0), color(g1), smoothstep(0.45, 0.7, big));
  col = mix(col, color(g2), smoothstep(0.6, 0.8, mx_noise_float(p.mul(0.07).add(5.0)).mul(0.5).add(0.5)).mul(0.6));
  col = col.mul(fine.mul(0.12).add(0.94));
  const gm = ctx.style === 'cel' ? new THREE.MeshToonNodeMaterial() : new THREE.MeshStandardNodeMaterial({ roughness: 0.95 });
  if (ctx.style === 'cel') (gm as THREE.MeshToonNodeMaterial).gradientMap = toonGradientMap();
  gm.colorNode = groundMarks(col, ctx.ground.nodes(), o.marks);
  groundMeshes(group, ctx, gm);
  // EDGE WALLS (owner 2026-11-02): a plain rock wall at the edge instead of the old energy barrier (o.barrier is unused now)
  const rockMat = (k: number) => { const m = ctx.style === 'cel' ? new THREE.MeshToonNodeMaterial({ gradientMap: toonGradientMap() }) : new THREE.MeshStandardNodeMaterial({ roughness: 1 });
    m.colorNode = mix(color(o.scenery || 0xb08a64).mul(0.6 * k), color(o.scenery || 0xb08a64).mul(k), smoothstep(0.3, 0.7, mx_noise_float(positionWorld.mul(0.15)).mul(0.5).add(0.5))); return m; };
  const wall = buildEdgeWall(group, ctx, [
    { parts: [{ geo: cliffPiece(11), mat: rockMat(1), outline: 0.012 }], spacing: [8, 12], w: [10, 14], h: [6, 9], d: [6, 8], wave: 0.3 },
    { parts: [{ geo: rubblePiece(5), mat: rockMat(0.9), outline: 0.02 }], spacing: [5, 9], w: [3, 6], h: [1.3, 2.5], d: [2, 3.4], chance: 0.8 },
  ], 9);
  if (o.scenery !== 0) {
    const mat = new THREE.MeshStandardNodeMaterial({ roughness: 1 });
    const n = mx_noise_float(positionWorld.mul(0.08)).mul(0.5).add(0.5);
    mat.colorNode = mix(color(o.scenery ?? 0xb08a64).mul(0.7), color(o.scenery ?? 0xb08a64), smoothstep(0.3, 0.7, n));
    const geo = new THREE.CylinderGeometry(0.75, 1, 1, 9, 1).translate(0, 0.5, 0);
    const rng = (i: number, k: number) => { const h = Math.sin(i * 12.9898 + k * 78.233) * 43758.5453; return h - Math.floor(h); };
    const NB = Math.round(28 * vistaRingK(165)), im = new THREE.InstancedMesh(geo, mat, NB), m = new THREE.Matrix4(), q = new THREE.Quaternion(), s = new THREE.Vector3(), v = new THREE.Vector3();
    for (let i = 0; i < NB; i++) {
      const a = (i / NB) * Math.PI * 2 + rng(i, 1) * 0.2, r = 125 + VISTA_SHIFT + rng(i, 2) * 80, rad = 9 + rng(i, 3) * 14;
      im.setMatrixAt(i, m.compose(v.set(Math.cos(a) * r, -2, Math.sin(a) * r), q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rng(i, 4) * 6), s.set(rad, 25 + rng(i, 5) * 50, rad)));
    }
    group.add(im);
  }
  return { group, sky, update: () => wall.update() };
}
